'use client';

import React, { useEffect, useRef } from 'react';

export default function ServicesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-up').forEach((el, i) => {
              setTimeout(() => el.classList.add('in-view'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  const services = [
    {
      id: '01',
      title: 'Sports Photography',
      description: 'High-speed action shots that freeze the decisive moment — from local leagues to professional competitions.',
      tags: ['Action', 'Motion', 'Competition'],
      symbol: '◎',
      accent: 'text-coral',
      color: 'from-charcoal to-ink',
      featured: true,
    },
    {
      id: '02',
      title: 'Product Photoshoots',
      description: 'Clean, compelling product imagery that drives conversions and elevates your brand identity.',
      tags: ['Commercial', 'Studio', 'Brand'],
      symbol: '◈',
      accent: 'text-vapor',
      color: 'from-ink to-charcoal',
      featured: false,
    },
    {
      id: '03',
      title: 'Portraits',
      description: 'Authentic, emotive portraits that capture personality — from headshots to personal editorial sessions.',
      tags: ['Headshots', 'Editorial', 'Personal'],
      symbol: '◉',
      accent: 'text-coral',
      color: 'from-charcoal to-[#1e1a18]',
      featured: false,
    },
    {
      id: '04',
      title: 'Events',
      description: 'Full-coverage event photography — candid moments, key highlights, and the energy of every occasion.',
      tags: ['Coverage', 'Candid', 'Highlights'],
      symbol: '◇',
      accent: 'text-concrete',
      color: 'from-ink to-charcoal',
      featured: false,
    },
  ];

  return (
    <section id="work" ref={sectionRef} className="bg-charcoal py-24 px-6 md:px-10 lg:px-14">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="reveal-up flex items-end justify-between mb-14">
          <div>
            <span className="section-label text-coral block mb-3">Services</span>
            <h2
              className="font-mono font-bold text-concrete leading-tight"
              style={{ fontSize: 'clamp(1.8rem, 4vw, 3.2rem)' }}
            >
              WHAT I<br />SHOOT BEST.
            </h2>
          </div>
          <span className="hidden md:block font-mono text-[0.6rem] tracking-[0.3em] uppercase text-concrete/25">
            Available for booking
          </span>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Featured large card */}
          <div
            className={`reveal-up md:col-span-2 bg-gradient-to-br ${services?.[0]?.color} border border-concrete/8 p-8 flex flex-col justify-between min-h-[280px] group cursor-default hover:border-coral/30 transition-all duration-500`}
          >
            <div className="flex items-start justify-between">
              <span className={`font-mono text-4xl ${services?.[0]?.accent} opacity-40 group-hover:opacity-70 transition-opacity duration-500`}>
                {services?.[0]?.symbol}
              </span>
              <span className="font-mono text-[0.55rem] tracking-[0.35em] uppercase text-concrete/30">
                {services?.[0]?.id}
              </span>
            </div>
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                {services?.[0]?.tags?.map((tag) => (
                  <span key={tag} className="font-mono text-[0.55rem] font-bold tracking-[0.25em] uppercase text-coral/70 border border-coral/20 px-2 py-0.5">
                    {tag}
                  </span>
                ))}
              </div>
              <h3
                className="font-mono font-bold text-concrete leading-tight mb-2"
                style={{ fontSize: 'clamp(1.3rem, 2.5vw, 2rem)' }}
              >
                {services?.[0]?.title}
              </h3>
              <p className="font-sans text-sm text-concrete/55 leading-relaxed max-w-sm mb-4">
                {services?.[0]?.description}
              </p>
              <a
                href="#contact"
                className="inline-block font-mono text-[0.65rem] font-bold tracking-[0.25em] uppercase text-ink bg-coral px-4 py-2 hover:bg-coral/80 transition-colors duration-300"
              >
                Book Now
              </a>
            </div>
          </div>

          {/* Smaller cards */}
          {services?.slice(1)?.map((service) => (
            <div
              key={service?.id}
              className={`reveal-up bg-gradient-to-br ${service?.color} border border-concrete/8 p-6 flex flex-col justify-between min-h-[200px] group cursor-default hover:border-coral/30 transition-all duration-500`}
            >
              <div className="flex items-start justify-between">
                <span className={`font-mono text-[1.8rem] font-bold ${service?.accent} opacity-20 group-hover:opacity-50 transition-opacity`}>
                  {service?.symbol}
                </span>
                <span className="font-mono text-[0.55rem] tracking-[0.35em] uppercase text-concrete/25">
                  {service?.id}
                </span>
              </div>
              <div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {service?.tags?.map((tag) => (
                    <span key={tag} className="font-mono text-[0.5rem] font-bold tracking-[0.2em] uppercase text-coral/60">
                      {tag}
                    </span>
                  ))}
                </div>
                <h3 className="font-mono font-bold text-concrete text-base leading-tight mb-1.5">
                  {service?.title}
                </h3>
                <p className="font-sans text-xs text-concrete/45 leading-relaxed mb-3">
                  {service?.description}
                </p>
                <a
                  href="#contact"
                  className="inline-block font-mono text-[0.6rem] font-bold tracking-[0.25em] uppercase text-ink bg-coral px-3 py-1.5 hover:bg-coral/80 transition-colors duration-300"
                >
                  Book Now
                </a>
              </div>
            </div>
          ))}
        </div>

        <p className="reveal-up mt-8 font-mono text-[0.6rem] tracking-[0.3em] uppercase text-concrete/25 text-center">
          Custom packages available — reach out to discuss your project
        </p>
      </div>
    </section>
  );
}
